import {
  Box,
  LinearProgress,
  List,
  ListItem,
  Typography,
} from '@material-ui/core'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StoreState } from 'store'
import { formatBrl } from '../../utils'
import moment from 'moment'
import { editingStartPatrimony } from 'store/patrimony/patrimonyActions'

import EmojiTransportationIcon from '@material-ui/icons/EmojiTransportation'

import useTypedSelector from '../../hooks/useTypedSelector'
import getPatrimony, { setFilters } from 'store/patrimony/patrimonyActions'

import { makeStyles } from '@material-ui/core/styles'
import InputBase from '@material-ui/core/InputBase'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import SearchIcon from '@material-ui/icons/Search'
import CancelIcon from '@material-ui/icons/Cancel'
import { PatrimonyActionTypes } from 'store/patrimony/patrimonyTypes'
import Card from '@material-ui/core/Card'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(5),

    background: '#fff',

    position: 'sticky',
    top: 0,
    minWidth: '275',
    zIndex: 9,
  },
  searchIcon: {
    color: '#888',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  cancelIcon: {
    color: '#ddd',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
}))

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout

  return function (...args: Parameters<T>): void {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }
}

export const PatrimonyList = () => {
  const classes = useStyles()
  const [inputValue, setInputValue] = useState('')

  const { patrimony, isLoading, isEditing, selectedIndex, filterPatrimony } =
    useSelector(({ patrimonyState }: StoreState) => ({
      patrimony: patrimonyState.patrimony,
      isLoading: patrimonyState.loadingPatrimony,
      isEditing: patrimonyState.isEditing,
      selectedIndex: patrimonyState.selectedIndex,
      filterPatrimony: patrimonyState.filterPatrimony,
    }))

  const filteredPatrimony = useMemo(() => {
    if (!filterPatrimony) {
      return patrimony
    }
    return patrimony.filter((item: any) =>
      item.description
        .toLowerCase()
        .includes(filterPatrimony.toLowerCase().trim())
    )
  }, [patrimony, filterPatrimony])

  const dispatch = useDispatch()
  const onClickItem = (index: number) => () => {
    if (!filterPatrimony) {
      dispatch(editingStartPatrimony(index))
    } else {
      const Patrimony = filteredPatrimony[index]
      const originalIndex = patrimony.findIndex(
        (item) => item.id === Patrimony.id
      )
      dispatch(editingStartPatrimony(originalIndex))
    }
  }

  const { filters } = useTypedSelector(({ patrimonyState }) => ({
    filters: patrimonyState.filters,
  }))

  const sendRequest = useCallback(
    (value: string) => {
      if (filters.dateFrom !== '') {
        dispatch(setFilters('', ''))
        dispatch(getPatrimony())
      }

      dispatch({
        type: PatrimonyActionTypes.FILTER_PATRIMONY,
        filterPatrimony: value,
      })
    },
    [dispatch, filters.dateFrom]
  )

  // memoize the debounce call with useMemo
  const debouncedSendRequest = useMemo(() => {
    return debounce(sendRequest, 300)
  }, [sendRequest])

  const onChange = (e: any) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSendRequest(value)
  }

  const handleClick = (): void => {
    setInputValue('')

    dispatch({
      type: PatrimonyActionTypes.EDITING_CANCEL,
    })

    dispatch({
      type: PatrimonyActionTypes.FILTER_PATRIMONY,
      filterPatrimony: '',
    })
  }

  return (
    <>
      <>
        <Card className={classes.root}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.input}
            name="search"
            placeholder="Pesquisar..."
            onChange={onChange}
            value={inputValue}
            autoComplete="new-password" // hack para desativar preenchimento automático
          />
          {!!inputValue ? (
            <IconButton onClick={handleClick}>
              <CancelIcon className={classes.cancelIcon} />
            </IconButton>
          ) : (
            <div>
              <p style={{ padding: 24 }} />
            </div>
          )}
        </Card>
        <Divider />
      </>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <List>
          {!patrimony.length ? (
            <Box
              display="flex"
              justifyContent="center"
              color="gray"
              marginTop={1}
            >
              <Typography
                variant="caption"
                align="center"
                children="Nenhum registro encontrado"
              />
            </Box>
          ) : (
            patrimony
              .filter((item: any) =>
                item.description
                  .toLowerCase()
                  .includes(filterPatrimony.toLowerCase())
              )
              .map((asset, i) => (
                <ListItem
                  style={{
                    boxShadow: '0px 4px 0px rgba(55, 55, 55, 0.1)',
                    height: 80,
                  }}
                  key={i}
                  button
                  onClick={onClickItem(i)}
                  selected={
                    !filterPatrimony && isEditing && selectedIndex === i
                  }
                >
                  <Box display="flex" flex={1} justifyContent="space-between">
                    <Box>
                      <Typography>{asset.description}</Typography>
                      <EmojiTransportationIcon
                        style={{ color: '#00b37e' }}
                      />{' '}
                      <Typography
                        variant="caption"
                        className="text-green-700"
                        children={formatBrl(Number(asset.acquisition_value))}
                      />
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                    >
                      <Typography variant="caption" children="Data aquisição" />
                      <Typography>
                        {moment(asset.acquisition_date.slice(0, 10)).format(
                          'DD/MM/YYYY'
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))
          )}
        </List>
      )}
    </>
  )
}
